import React, { useCallback, useEffect, useRef, useState } from "react";
import * as Mui from "@mui/material";
import moment from "moment";
import { useSelector } from "react-redux";
import { CheckRounded, ErrorOutlineOutlined, HourglassEmptyRounded, ListAltOutlined, RemoveRounded } from "@mui/icons-material";
import { showToast } from "services/utils/Status";
import { RequestInfoBanner } from "components/DesignSystem/RequestInfoBanner";
import { GlobalConfirmDialog } from "components/DesignSystem/sharedUiPatterns";
import { SecondaryButton } from "components/CustomComponents/DesignSystemButtons";
import { useSavedViewSelector } from "components/CustomComponents/useSavedViewSelector";
import { designColors, designType, designWeights } from "services/constants/designTokens";
import { ptEmployeeHeader } from "pages/PayRoll/PayRollHeaders";
import { Field } from "pages/PayRoll/PayInputs/Shared/formUi";
import { SelectField } from "pages/PayRoll/PayInputs/Shared/fieldInputs";
import { ToneChip } from "pages/PayRoll/Salary/Shared/salaryUi";
import { StatCardGrid, StatutoryList, StatutoryPage, initialsOf } from "pages/PayRoll/Shared/statutoryUi";
import { endPtExemption, fetchPtEmployee, fetchPtEmployees, monthOptions } from "./ptEmployeesApi";
import { MarkExemptPanel, PtDetailsPanel } from "./PtEmployeePanels";
import { OpenSettingsButton } from "pages/PayRoll/Shared/settingsSwitch";

// Payroll > Statutory > Professional Tax > Employees (screens PT2–PT2b):
// each employee's PT site (from the work location — never edited here),
// date of birth for the age exemption, PT status and any exemption, worked
// out live by the server for the first open payroll month
// (ptEmployeesApi.js). Search, cards and paging go to the
// server. HR marks or ends exemptions here. PT amounts are in Reports.

const ALL = "all";
const PAGE_SIZE = 50;
const EXPORT_PAGE_SIZE = 200;
const SEARCH_DELAY = 400;

export const StatutoryPT = () => {
  const { domain, topbarCompanyId } = useSelector((state) => state.tokenReducer);
  // Fresh state per company.
  return <PtEmployeesScreen key={`${domain}:${topbarCompanyId}`} domain={domain} companyId={topbarCompanyId} />;
};

const PtEmployeesScreen = ({ domain, companyId }) => {
  const { userDetails, topbarCompanyName } = useSelector((state) => state.tokenReducer);
  const [card, setCard] = useState(ALL);
  const [search, setSearch] = useState("");
  const [query, setQuery] = useState("");
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(PAGE_SIZE);
  const [reloadKey, setReloadKey] = useState(0);
  const [list, setList] = useState({ loading: true, error: "", rows: [], count: 0, cards: {}, month_label: "" });
  const [viewId, setViewId] = useState(null);
  const [details, setDetails] = useState({ loading: false, error: "", detail: null });
  const [detailsKey, setDetailsKey] = useState(0);
  const [markOpen, setMarkOpen] = useState(false);
  const [endOpen, setEndOpen] = useState(false);
  const [endMonth, setEndMonth] = useState("");
  const [endError, setEndError] = useState("");
  const [ending, setEnding] = useState(false);
  const firstSearch = useRef(true);

  const { visibleHeaders, titleOverride } = useSavedViewSelector({
    domain,
    userId: userDetails?.id,
    storageKey: "pt_employees_saved_views",
    fullHeaders: ptEmployeeHeader,
  });

  // Search waits for typing to pause, then starts again from page 1.
  useEffect(() => {
    if (firstSearch.current) {
      firstSearch.current = false;
      return undefined;
    }
    const timer = setTimeout(() => {
      setQuery(search.trim());
      setPage(0);
    }, SEARCH_DELAY);
    return () => clearTimeout(timer);
  }, [search]);

  const status = card === ALL ? undefined : card;
  useEffect(() => {
    const controller = new AbortController();
    setList((current) => ({ ...current, loading: true, error: "" }));
    fetchPtEmployees({ domain, companyId, status, search: query, page: page + 1, pageSize, signal: controller.signal })
      .then((data) => setList({ loading: false, error: "", ...data }))
      .catch((error) => {
        if (controller.signal.aborted) return;
        setList((current) => ({ ...current, loading: false, error: error.message || "Could not load PT employees." }));
      });
    return () => controller.abort();
  }, [domain, companyId, status, query, page, pageSize, reloadKey]);

  useEffect(() => {
    if (viewId == null) {
      setDetails({ loading: false, error: "", detail: null });
      return undefined;
    }
    const controller = new AbortController();
    setDetails((current) => ({ loading: true, error: "", detail: current.detail?.row.id === viewId ? current.detail : null }));
    fetchPtEmployee({ domain, companyId, employeeId: viewId, signal: controller.signal })
      .then((detail) => setDetails({ loading: false, error: "", detail }))
      .catch((error) => {
        if (controller.signal.aborted) return;
        setDetails({ loading: false, error: error.message || "Could not load the PT details.", detail: null });
      });
    return () => controller.abort();
  }, [domain, companyId, viewId, detailsKey]);

  const reloadAll = () => {
    setReloadKey((key) => key + 1);
    setDetailsKey((key) => key + 1);
  };
  const resetPage = (setter) => (value) => {
    setter(value);
    setPage(0);
  };

  // Export: every matching row, page by page.
  const exportRows = useCallback(async () => {
    const all = [];
    for (let next = 1; ; next += 1) {
      // eslint-disable-next-line no-await-in-loop
      const data = await fetchPtEmployees({ domain, companyId, status, search: query, page: next, pageSize: EXPORT_PAGE_SIZE });
      all.push(...data.rows.map(exportRow));
      if (!data.rows.length || all.length >= data.count) return all;
    }
  }, [domain, companyId, status, query]);

  const detail = details.detail;
  // The last exempt month: from the month before the first open month,
  // and not before the exemption's first month.
  const endOptions = (() => {
    if (!detail?.month) return [];
    const before = moment(`${detail.month}-01`).subtract(1, "month").format("YYYY-MM");
    const first = detail.exemption.from_month && detail.exemption.from_month > before ? detail.exemption.from_month : before;
    return monthOptions(first);
  })();

  const endExemption = async () => {
    if (!endMonth) {
      setEndError("Choose the last exempt month");
      return;
    }
    if (ending) return;
    setEnding(true);
    try {
      const result = await endPtExemption({ domain, companyId, exemptionId: detail.exemption.id, toMonth: endMonth });
      showToast("success", result?.message || "Exemption ended.");
      setEndOpen(false);
      reloadAll();
    } catch (error) {
      if (error.fields?.to) setEndError(error.fields.to);
      else showToast("error", error.message || "Couldn't end the exemption.");
    } finally {
      setEnding(false);
    }
  };

  const cards = [
    { key: ALL, title: "All", value: num(list.cards.all), icon: ListAltOutlined, tone: "blue" },
    { key: "PAYS_PT", title: "Pays PT", value: num(list.cards.pays_pt), icon: CheckRounded, tone: "green" },
    { key: "EXEMPT", title: "Exempt", value: num(list.cards.exempt), icon: RemoveRounded, tone: "amber" },
    { key: "SITE_NOT_SET_UP", title: "Site not set up", value: num(list.cards.site_not_set_up), icon: ErrorOutlineOutlined, tone: "red" },
    // Held employees are inside All; their card shows only when there are any.
    ...(list.cards.held || card === "HELD" ? [{ key: "HELD", title: "Held", value: num(list.cards.held), icon: HourglassEmptyRounded, tone: "purple" }] : []),
  ];

  const renderCell = (row, key) => {
    switch (key) {
      case "name":
        return (
          <Mui.Stack direction="row" spacing={1} alignItems="center">
            <Mui.Avatar sx={{ width: 28, height: 28, fontSize: 11, fontWeight: 700, backgroundColor: designColors.slate100, color: designColors.slate700 }}>
              {initialsOf(row.name)}
            </Mui.Avatar>
            <Mui.Typography sx={{ ...designType.tableBodyCell, fontWeight: designWeights.medium, whiteSpace: "nowrap" }}>{row.name}</Mui.Typography>
          </Mui.Stack>
        );
      case "dob_label":
        return row.dob ? row.dob_label : <Mui.Box component="span" sx={{ color: "#DC2626" }}>Missing</Mui.Box>;
      case "pt_wage":
        return row.pt_wage == null ? "—" : num(row.pt_wage);
      case "pt_status":
        return <ToneChip label={row.pt_status} tone={row.pt_status_tone} />;
      default:
        return row[key] || "—";
    }
  };

  const monthLabel = list.month_label;
  return (
    <StatutoryPage
      breadcrumb={["Payroll", "Statutory", "Professional Tax", "Employees"]}
      title="PT Employees"
      switchAction={<OpenSettingsButton group="statutory" item="pt" />}
      subtitle={`Each employee's PT site and any exemption${monthLabel ? ` for ${monthLabel} (the first open payroll month)` : ""}. The site comes from the work location on the last day of the month. PT amounts are in Reports › Statutory Reports › PT.`}
    >
      <StatCardGrid cards={cards} value={card} onChange={resetPage(setCard)} />

      <Mui.Stack spacing={1.5} sx={{ mb: 1.5 }}>
        {list.error ? (
          <RequestInfoBanner
            tone="danger"
            title="Couldn't load PT employees."
            body={list.error}
            action={<SecondaryButton onClick={() => setReloadKey((key) => key + 1)} sx={{ whiteSpace: "nowrap" }}>Retry</SecondaryButton>}
          />
        ) : null}
        {!list.error && list.cards.site_not_set_up ? (
          <RequestInfoBanner
            tone="danger"
            body={`${num(list.cards.site_not_set_up)} ${list.cards.site_not_set_up === 1 ? "employee works at a site" : "employees work at sites"} with no PT set-up, so no PT is deducted there. Set it up in Payroll Settings › Statutory › Professional Tax.`}
          />
        ) : null}
        {!list.error && list.cards.held ? (
          <RequestInfoBanner
            tone="warning"
            body={`${num(list.cards.held)} ${list.cards.held === 1 ? "employee is" : "employees are"} held for missing data (date of birth, gender or site). Their payroll is held until it is added in the employee profile.`}
          />
        ) : null}
      </Mui.Stack>

      <StatutoryList
        title={`PT Employees ${monthLabel}`.trim()}
        headers={visibleHeaders}
        titleOverride={titleOverride}
        rows={list.rows}
        loading={list.loading}
        renderCell={renderCell}
        onRowClick={(row) => setViewId(row.id)}
        selectedKey={viewId}
        itemLabel="employees"
        searchPlaceholder="Search name or ID"
        metadata={{ "Company Name": topbarCompanyName || "N/A", Month: monthLabel || "–", "Report Generated On": moment().format("DD/MM/YYYY HH:mm:ss") }}
        server={{
          search,
          onSearchChange: setSearch,
          page,
          rowsPerPage: pageSize,
          count: list.count,
          onPageChange: setPage,
          onRowsPerPageChange: (size) => {
            setPageSize(size);
            setPage(0);
          },
          exportRows,
        }}
      />
      <Mui.Box sx={{ mt: 1.5 }}>
        <RequestInfoBanner body="Exemptions available for each employee are the ones set up for their site (Payroll Settings › Professional Tax). Age exemptions are applied automatically from the date of birth." />
      </Mui.Box>

      <PtDetailsPanel
        open={viewId != null && !markOpen}
        loading={details.loading && !detail}
        error={details.error}
        detail={detail}
        onRetry={() => setDetailsKey((key) => key + 1)}
        onClose={() => setViewId(null)}
        onMarkExempt={() => setMarkOpen(true)}
        onEndExemption={() => {
          setEndMonth(endOptions[0]?.value || "");
          setEndError("");
          setEndOpen(true);
        }}
      />
      <MarkExemptPanel
        open={markOpen && Boolean(detail)}
        domain={domain}
        companyId={companyId}
        row={detail?.row}
        onClose={() => setMarkOpen(false)}
        onSaved={() => {
          setMarkOpen(false);
          reloadAll();
        }}
      />
      <GlobalConfirmDialog
        open={endOpen}
        title="End exemption"
        onCancel={() => setEndOpen(false)}
        onConfirm={endExemption}
        confirmLabel={ending ? "Ending…" : "End exemption"}
        cancelLabel="Keep"
        maxWidth="sm"
      >
        <Mui.Stack spacing={2}>
          <Mui.Typography sx={designType.helperText}>
            {detail?.row.name} pays PT again from the month after the one you choose. Months already exempt stay exempt.
          </Mui.Typography>
          <Field label="Last exempt month" error={endError}>
            <SelectField
              value={endMonth}
              onChange={(value) => {
                if (!value) return;
                setEndMonth(value);
                setEndError("");
              }}
              options={endOptions}
              error={endError}
              disablePortal={false}
            />
          </Field>
        </Mui.Stack>
      </GlobalConfirmDialog>
    </StatutoryPage>
  );
};

const num = (value) => Number(value || 0).toLocaleString("en-IN");

// Download rows: plain text for the columns that render chips or dashes.
const exportRow = (row) => ({
  ...row,
  dob_label: row.dob ? row.dob_label : "Missing",
  pt_wage: row.pt_wage ?? "",
  frequency_label: row.frequency_label || "—",
  exemption_label: row.exemption_label || "—",
});
